const FACTORS = {
  length: {
    mm: 0.001,
    cm: 0.01,
    m: 1,
    km: 1000,
    in: 0.0254,
    ft: 0.3048,
    yd: 0.9144,
    mi: 1609.344,
  },
  area: {
    mm2: 1e-6,
    cm2: 1e-4,
    m2: 1,
    km2: 1e6,
    in2: 0.00064516,
    ft2: 0.09290304,
    yd2: 0.83612736,
    ac: 4046.8564224,
  },
  volume: {
    ml: 0.000001,
    l: 0.001,
    m3: 1,
    fl_oz: 0.0000295735295625,
    pt: 0.000473176473,
    qt: 0.000946352946,
    gal: 0.003785411784,
  },
  mass: {
    mg: 0.000001,
    g: 0.001,
    kg: 1,
    t: 1000,
    oz: 0.028349523125,
    lb: 0.45359237,
    st: 6.35029318,
  },
  force: {
    n: 1,
    kn: 1000,
    mn: 1e6,
    lbf: 4.4482216152605,
  },
  pressure: {
    pa: 1,
    kpa: 1000,
    mpa: 1e6,
    bar: 1e5,
    psi: 6894.757293168,
    atm: 101325,
    torr: 133.322368421,
  },
  energy: {
    j: 1,
    kj: 1000,
    mj: 1e6,
    cal: 4.184,
    kcal: 4184,
    kwh: 3.6e6,
  },
  power: {
    w: 1,
    kw: 1000,
    mw: 1e6,
    hp: 745.69987158227,
  },
  speed: {
    mps: 1,
    kmh: 1000 / 3600,
    mph: 1609.344 / 3600,
    knots: 1852 / 3600,
  },
  torque: {
    nm: 1,
    ftlb: 1.3558179483314,
    inlb: 0.1129848290276167,
  },
  flow: {
    m3s: 1,
    m3h: 1 / 3600,
    lpm: 1 / 60000,
    m3min: 1 / 60,
    lps: 0.001,
    mls: 1e-6,
    cfm: 0.0004719474432,
    ft3h: 0.0004719474432 / 60,
    gpm: 0.003785411784 / 60,
    galh: 0.003785411784 / 3600,
    in3s: 1.6387064e-5,
    bblh: 0.158987294928 / 3600,
  },
};

function toKelvin(value, unit) {
  if (unit === 'k') return value;
  if (unit === 'c') return value + 273.15;
  if (unit === 'f') return ((value - 32) * 5) / 9 + 273.15;
  throw new Error(`Unknown temperature unit: ${unit}`);
}

function fromKelvin(value, unit) {
  if (unit === 'k') return value;
  if (unit === 'c') return value - 273.15;
  if (unit === 'f') return ((value - 273.15) * 9) / 5 + 32;
  throw new Error(`Unknown temperature unit: ${unit}`);
}

export function convert(value, fromUnit, toUnit, category) {
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) {
    throw new Error('Please enter a valid number');
  }

  if (fromUnit === toUnit) return numeric;

  if (category === 'temperature') {
    return fromKelvin(toKelvin(numeric, fromUnit), toUnit);
  }

  const table = FACTORS[category];
  if (!table?.[fromUnit] || !table?.[toUnit]) {
    throw new Error(`Conversion from ${fromUnit} to ${toUnit} is not supported`);
  }

  return (numeric * table[fromUnit]) / table[toUnit];
}

export function formatResult(value, unit) {
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return '';
  if (unit === 'c' || unit === 'f') return numeric.toFixed(1);
  return numeric.toFixed(2);
}

export const categories = [
  { value: 'area', label: 'Area' },
  { value: 'energy', label: 'Energy' },
  { value: 'flow', label: 'Flow Rate' },
  { value: 'force', label: 'Force' },
  { value: 'length', label: 'Length' },
  { value: 'mass', label: 'Mass' },
  { value: 'power', label: 'Power' },
  { value: 'pressure', label: 'Pressure' },
  { value: 'speed', label: 'Speed' },
  { value: 'temperature', label: 'Temperature' },
  { value: 'torque', label: 'Torque' },
  { value: 'volume', label: 'Volume' },
];

export const units = {
  area: [
    { value: 'ac', label: 'Acres (ac)' },
    { value: 'cm2', label: 'Square centimetres (cm²)' },
    { value: 'ft2', label: 'Square feet (ft²)' },
    { value: 'in2', label: 'Square inches (in²)' },
    { value: 'km2', label: 'Square kilometres (km²)' },
    { value: 'm2', label: 'Square metres (m²)' },
    { value: 'mm2', label: 'Square millimetres (mm²)' },
    { value: 'yd2', label: 'Square yards (yd²)' },
  ],
  energy: [
    { value: 'cal', label: 'Calories (cal)' },
    { value: 'j', label: 'Joules (J)' },
    { value: 'kcal', label: 'Kilocalories (kcal)' },
    { value: 'kj', label: 'Kilojoules (kJ)' },
    { value: 'kwh', label: 'Kilowatt hours (kWh)' },
    { value: 'mj', label: 'Megajoules (MJ)' },
  ],
  flow: [
    { value: 'bblh', label: 'Barrels per hour (bbl/h)' },
    { value: 'cfm', label: 'Cubic feet per minute (CFM)' },
    { value: 'ft3h', label: 'Cubic feet per hour (ft³/h)' },
    { value: 'galh', label: 'Gallons per hour (gal/h)' },
    { value: 'gpm', label: 'Gallons per minute (GPM)' },
    { value: 'in3s', label: 'Cubic inches per second (in³/s)' },
    { value: 'lpm', label: 'Litres per minute (L/min)' },
    { value: 'lps', label: 'Litres per second (L/s)' },
    { value: 'm3h', label: 'Cubic metres per hour (m³/h)' },
    { value: 'm3min', label: 'Cubic metres per minute (m³/min)' },
    { value: 'm3s', label: 'Cubic metres per second (m³/s)' },
    { value: 'mls', label: 'Millilitres per second (mL/s)' },
  ],
  force: [
    { value: 'kn', label: 'Kilonewtons (kN)' },
    { value: 'lbf', label: 'Pound-force (lbf)' },
    { value: 'mn', label: 'Meganewtons (MN)' },
    { value: 'n', label: 'Newtons (N)' },
  ],
  length: [
    { value: 'cm', label: 'Centimetres (cm)' },
    { value: 'ft', label: 'Feet (ft)' },
    { value: 'in', label: 'Inches (in)' },
    { value: 'km', label: 'Kilometres (km)' },
    { value: 'm', label: 'Metres (m)' },
    { value: 'mi', label: 'Miles (mi)' },
    { value: 'mm', label: 'Millimetres (mm)' },
    { value: 'yd', label: 'Yards (yd)' },
  ],
  mass: [
    { value: 'g', label: 'Grams (g)' },
    { value: 'kg', label: 'Kilograms (kg)' },
    { value: 'lb', label: 'Pounds (lb)' },
    { value: 'mg', label: 'Milligrams (mg)' },
    { value: 'oz', label: 'Ounces (oz)' },
    { value: 'st', label: 'Stone (st)' },
    { value: 't', label: 'Metric tonnes (t)' },
  ],
  power: [
    { value: 'hp', label: 'Horsepower (hp)' },
    { value: 'kw', label: 'Kilowatts (kW)' },
    { value: 'mw', label: 'Megawatts (MW)' },
    { value: 'w', label: 'Watts (W)' },
  ],
  pressure: [
    { value: 'atm', label: 'Atmosphere (atm)' },
    { value: 'bar', label: 'Bar' },
    { value: 'kpa', label: 'Kilopascal (kPa)' },
    { value: 'mpa', label: 'Megapascal (MPa)' },
    { value: 'psi', label: 'PSI' },
    { value: 'torr', label: 'Torr' },
  ],
  speed: [
    { value: 'kmh', label: 'Kilometres per hour (km/h)' },
    { value: 'knots', label: 'Knots' },
    { value: 'mph', label: 'Miles per hour (mph)' },
    { value: 'mps', label: 'Metres per second (m/s)' },
  ],
  temperature: [
    { value: 'c', label: 'Celsius (°C)' },
    { value: 'f', label: 'Fahrenheit (°F)' },
    { value: 'k', label: 'Kelvin (K)' },
  ],
  torque: [
    { value: 'ftlb', label: 'Foot pounds (ft·lb)' },
    { value: 'inlb', label: 'Inch pounds (in·lb)' },
    { value: 'nm', label: 'Newton metres (N·m)' },
  ],
  volume: [
    { value: 'fl_oz', label: 'Fluid ounces (fl oz)' },
    { value: 'gal', label: 'Gallons (gal)' },
    { value: 'l', label: 'Litres (L)' },
    { value: 'm3', label: 'Cubic metres (m³)' },
    { value: 'ml', label: 'Millilitres (mL)' },
    { value: 'pt', label: 'Pints (pt)' },
    { value: 'qt', label: 'Quarts (qt)' },
  ],
};
